import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import UsersTable from './UsersTable';
import ProjectsTable from './ProjectsTable';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

const AdminPanel = ({ onLogout, initialSection = null }) => {
  const [userCount, setUserCount] = useState(0);
  const [projectCount, setProjectCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [refreshingReports, setRefreshingReports] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/admin/users') setActiveSection('users');
    else if (location.pathname === '/admin/projects') setActiveSection('projects');
    else if (location.pathname === '/admin') setActiveSection(null);
  }, [location.pathname]);

  const fetchCounts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { if (onLogout) onLogout(); return; }
      const [usersResponse, projectsResponse] = await Promise.all([
        axios.get(`${API_URL}/users/count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/projects/count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUserCount(usersResponse.data.count);
      setProjectCount(projectsResponse.data.count);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { if (onLogout) onLogout(); return; }
      else if (err.response?.status === 403) setError('Access denied. Admin privileges required.');
      else setError('Failed to load admin data.');
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCounts(); }, []);

  const handleBackToCards = () => { setActiveSection(null); navigate('/admin'); fetchCounts(); };

  const handleRefreshReports = async () => {
    setRefreshingReports(true);
    setRefreshMessage(null);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { if (onLogout) onLogout(); return; }
      const response = await axios.post(`${API_URL}/refresh-reports`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRefreshMessage({ type: 'success', text: response.data.success ? response.data.message : response.data.message });
    } catch (err) {
      if (err.response?.status === 401) { if (onLogout) onLogout(); return; }
      setRefreshMessage({ type: 'error', text: err.response?.data?.message || 'Failed to refresh reports' });
    } finally {
      setRefreshingReports(false);
      setTimeout(() => setRefreshMessage(null), 5000);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-96"><LoadingSpinner /></div>;

  if (error) return (
    <div className="p-4">
      <div className="p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400">
        {error}
      </div>
      <button onClick={() => navigate('/')} className="mt-4 text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800">
        Back to Main Page
      </button>
    </div>
  );

  if (activeSection === 'users') return <UsersTable onBack={handleBackToCards} onLogout={onLogout} />;
  if (activeSection === 'projects') return <ProjectsTable onBack={handleBackToCards} onLogout={onLogout} />;

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage your application settings and users</p>
      </div>

      {refreshMessage && (
        <div className={`p-4 mb-4 text-sm rounded-lg ${refreshMessage.type === 'success' ? 'text-green-800 bg-green-50 dark:text-green-400' : 'text-red-800 bg-red-50 dark:text-red-400'} dark:bg-gray-800`}>
          {refreshMessage.text}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Users Card */}
        <div onClick={() => { setActiveSection('users'); navigate('/admin/users'); }}
          className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center">
            <div className="inline-flex flex-shrink-0 justify-center items-center w-12 h-12 text-primary-600 bg-primary-100 rounded-lg dark:text-primary-300 dark:bg-primary-900">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" /></svg>
            </div>
            <div className="ml-4">
              <h3 className="mb-1 text-xl font-bold text-gray-900 dark:text-white">{userCount}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Users</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm font-medium text-primary-600 dark:text-primary-500">
            Manage Users
            <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
          </div>
        </div>

        {/* Projects Card */}
        <div onClick={() => { setActiveSection('projects'); navigate('/admin/projects'); }}
          className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center">
            <div className="inline-flex flex-shrink-0 justify-center items-center w-12 h-12 text-green-600 bg-green-100 rounded-lg dark:text-green-300 dark:bg-green-900">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" /><path d="M6 12a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H2h2a2 2 0 002-2v-2z" /></svg>
            </div>
            <div className="ml-4">
              <h3 className="mb-1 text-xl font-bold text-gray-900 dark:text-white">{projectCount}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Projects</p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm font-medium text-primary-600 dark:text-primary-500">
            Manage Projects
            <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
          </div>
        </div>

        {/* Refresh Reports Card */}
        <div onClick={!refreshingReports ? handleRefreshReports : undefined}
          className={`p-6 bg-white border border-gray-200 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow dark:border-gray-700 dark:bg-gray-800 ${refreshingReports ? 'opacity-70' : ''}`}>
          <div className="flex items-center">
            <div className="inline-flex flex-shrink-0 justify-center items-center w-12 h-12 text-orange-600 bg-orange-100 rounded-lg dark:text-orange-300 dark:bg-orange-900">
              <svg className={`w-6 h-6 ${refreshingReports ? 'animate-spin' : ''}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>
            </div>
            <div className="ml-4">
              <h3 className="mb-1 text-lg font-bold text-gray-900 dark:text-white">Refresh Reports</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {refreshingReports ? 'Fetching from S3...' : 'Fetch latest from S3'}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm font-medium text-primary-600 dark:text-primary-500">
            {refreshingReports ? 'Refreshing...' : 'Refresh Now'}
            <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
